import { StyledSelect } from "@ando/ui";
/**
 * EmailTemplatePicker — a thin dropdown mounted ABOVE the email envelope
 * preview in the focused action view for ``send_email`` / ``send_cold_email``.
 *
 * On select it drops the chosen template's subject + body into the controlled
 * ActionDrawer values via ``onChange`` (the same mechanism the envelope inputs
 * use), so the operator can then tweak the fields inline before Conferma. We do
 * a simple client-side ``{{field}}`` passthrough (no merge resolution) — the BE
 * re-renders merge fields on the cold bulk path; for the single composer the
 * raw template text is exactly what the operator edits.
 *
 * ``cold`` templates surface for ``send_cold_email``; everything else gets the
 * ``standard`` library. Lives at the FocusedActionView level (not in @ando/ui,
 * which must not depend on app feature hooks).
 */
import type { ActionFieldValue } from "@ando/ui";
import { useLingui } from "@lingui/react/macro";
import { Mails } from "lucide-react";
import * as React from "react";

import { useEmailTemplates } from "@/features/sales";

const SELECT_CLS =
  "rounded-md border border-hairline bg-card px-2.5 py-1.5 text-[12.5px] text-ink-1 focus:outline-none focus:ring-2 focus:ring-ando-orange/40";

export interface EmailTemplatePickerProps {
  /** ``send_cold_email`` surfaces the cold library; else the standard one. */
  readonly actionType: "send_email" | "send_cold_email";
  /** Live ActionDrawer values (so we merge, not clobber, other envelope fields). */
  readonly values: Readonly<Record<string, ActionFieldValue>>;
  /** Controlled setter — same callback the envelope inputs bind to. */
  readonly onChange: (next: Record<string, ActionFieldValue>) => void;
  readonly disabled?: boolean;
}

export function EmailTemplatePicker({
  actionType,
  values,
  onChange,
  disabled,
}: EmailTemplatePickerProps): React.ReactElement | null {
  const { t } = useLingui();
  const kind = actionType === "send_cold_email" ? "cold" : "standard";
  const { data: templates, isLoading } = useEmailTemplates(kind);
  const [selectedId, setSelectedId] = React.useState<string>("");

  // No templates → render nothing (don't clutter the composer with an empty
  // dropdown). Stay mounted while loading so the slot doesn't jump.
  if (!isLoading && (!templates || templates.length === 0)) return null;

  function handleSelect(id: string) {
    setSelectedId(id);
    if (!id || !templates) return;
    const tpl = templates.find((t) => t.id === id);
    if (!tpl) return;
    // Merge into the live values so ``from_account`` / ``to`` / ``cc`` stay put.
    onChange({ ...values, subject: tpl.subject, body: tpl.body });
  }

  return (
    <div className="flex items-center gap-2 rounded-lg border border-hairline bg-cream/40 px-3 py-2">
      <Mails size={14} strokeWidth={2} className="shrink-0 text-ink-3" aria-hidden />
      <label className="flex flex-1 items-center gap-2 text-[12px] text-ink-2">
        <span className="shrink-0 font-medium">{t`Modello`}</span>
        <StyledSelect
          className={`${SELECT_CLS} flex-1`}
          value={selectedId}
          onChange={(e) => handleSelect(e.target.value)}
          disabled={disabled || isLoading}
          aria-label={t`Compila da un modello email`}
        >
          <option value="">
            {isLoading ? t`Caricamento…` : t`Compila da un modello…`}
          </option>
          {templates?.map((tpl) => (
            <option key={tpl.id} value={tpl.id}>
              {tpl.owner_user_id == null ? t`Org · ` : t`Mio · `}
              {tpl.name}
            </option>
          ))}
        </StyledSelect>
      </label>
    </div>
  );
}
